// User-facing event tracking: pageview, engagement, outbound, downloads, conversions.

const DOWNLOAD_EXT = /\.(pdf|docx?|xlsx?|pptx?|zip|tar\.gz|tgz|mp3|mp4|wav|svg|jpg|jpeg|png|webp)(\?|#|$)/i;

export function attachEventTrackers(queue) {
  const pageStart = performance.now();
  const url = location.pathname + location.search;

  // Pageview — fire immediately.
  queue.pushEvent({
    type: "pageview",
    url,
    t: Date.now(),
    payload: {
      title: document.title,
      lang: document.documentElement.lang || null,
    },
  });

  attachScrollDepth(queue, url);
  attachTimeOnPage(queue, url, pageStart);
  attachLinkAndFormClicks(queue);
}

function attachScrollDepth(queue, url) {
  const thresholds = [25, 50, 75, 100];
  const fired = new Set();
  const onScroll = throttle(() => {
    const doc = document.documentElement;
    const scrollTop = window.scrollY || doc.scrollTop;
    const max = Math.max(0, doc.scrollHeight - window.innerHeight);
    if (max === 0) return;
    const percent = Math.min(100, Math.round((scrollTop / max) * 100));
    for (const t of thresholds) {
      if (percent >= t && !fired.has(t)) {
        fired.add(t);
        queue.pushEvent({
          type: "scroll",
          url,
          t: Date.now(),
          payload: { depth: t },
        });
      }
    }
  }, 200);
  window.addEventListener("scroll", onScroll, { passive: true });
}

function attachTimeOnPage(queue, url, pageStart) {
  window.addEventListener("pagehide", () => {
    const seconds = Math.round((performance.now() - pageStart) / 1000);
    if (seconds < 1 || seconds > 7200) return;
    queue.pushEvent({
      type: "time_on_page",
      url,
      t: Date.now(),
      payload: { seconds },
    });
  });
}

function attachLinkAndFormClicks(queue) {
  document.addEventListener("click", (e) => {
    const a = e.target && e.target.closest && e.target.closest("a[href]");
    if (a) handleLinkClick(queue, a);

    const conv = e.target && e.target.closest && e.target.closest("[data-track]");
    if (conv) handleConversionClick(queue, conv);
  }, true);

  document.addEventListener("submit", (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    const kind = form.getAttribute("data-track-form");
    if (!kind) return;
    queue.pushEvent({
      type: "form_submit",
      url: location.pathname + location.search,
      t: Date.now(),
      payload: { form: kind },
    });
  }, true);
}

function handleLinkClick(queue, a) {
  const href = a.getAttribute("href");
  if (!href || href.startsWith("#") || href.startsWith("mailto:") || href.startsWith("tel:")) return;

  let url;
  try { url = new URL(a.href, location.href); } catch (_) { return; }

  // Download detection (always tracked, even for same-host).
  if (DOWNLOAD_EXT.test(url.pathname)) {
    queue.pushEvent({
      type: "download",
      url: url.pathname + url.search,
      t: Date.now(),
      payload: { href: url.href },
    });
    return;
  }

  // Outbound: different host or subdomain.
  if (url.host && url.host !== location.host) {
    queue.pushEvent({
      type: "outbound",
      url: location.pathname + location.search,
      t: Date.now(),
      payload: { href: url.href, host: url.host },
    });
  }
}

function handleConversionClick(queue, el) {
  const kind = el.getAttribute("data-track");
  if (!kind) return;
  const type = kind === "donate" ? "donate_click"
    : kind === "search" ? "search"
    : kind === "lang" ? "lang_switch"
    : "custom";
  queue.pushEvent({
    type,
    url: location.pathname + location.search,
    t: Date.now(),
    payload: { name: kind },
  });
}

function throttle(fn, wait) {
  let last = 0;
  let scheduled = null;
  return function () {
    const now = Date.now();
    const remaining = wait - (now - last);
    if (remaining <= 0) {
      last = now;
      fn.apply(this, arguments);
    } else if (!scheduled) {
      scheduled = window.setTimeout(() => {
        last = Date.now();
        scheduled = null;
        fn.apply(this, arguments);
      }, remaining);
    }
  };
}
